import { useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import SlideshowRoundedIcon from "@mui/icons-material/SlideshowRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  createStudyPlanCall,
  extractStudyTopicsCall,
  uploadStudyPlanMaterialCall,
} from "../../redux/api/studyPlan.api";
import { MATERIAL_LANGUAGES, STUDY_SUBJECTS } from "./data";
import StudyShell from "./StudyShell";

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
const MAX_UPLOAD_MB = 50;

const GOALS = [
  { id: "exam", title: "Pass an exam", body: "A date, a name, and a plan that gets you there." },
  { id: "mastery", title: "Master the subject", body: "Go deeper than the next test." },
  { id: "general", title: "Learn as I go", body: "A steady path through the course." },
];

const LEVELS = [
  { id: "beginner", label: "Beginner", emoji: "🌱" },
  { id: "intermediate", label: "Intermediate", emoji: "📘" },
  { id: "advanced", label: "Advanced", emoji: "🎓" },
];

const MASTERY = [
  { id: "familiar", label: "Familiar", emoji: "👀" },
  { id: "practiced", label: "Practiced", emoji: "✍️" },
  { id: "confident", label: "Confident", emoji: "💪" },
];

const PREFS = [
  { id: "smart_text", label: "Smart text" },
  { id: "podcast", label: "Podcast" },
  { id: "practice", label: "Practice questions" },
];

const UPLOAD_OPTIONS = [
  {
    kind: "pdf",
    label: "PDF",
    sub: "Documents",
    accent: "#E53935",
    Icon: PictureAsPdfRoundedIcon,
    accept: ".pdf,application/pdf",
  },
  {
    kind: "ppt",
    label: "PowerPoint",
    sub: "PPT, PPTX",
    accent: "#F57C00",
    Icon: SlideshowRoundedIcon,
    accept:
      ".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation",
  },
  {
    kind: "doc",
    label: "Word",
    sub: "DOC, DOCX",
    accent: "#1565C0",
    Icon: DescriptionRoundedIcon,
    accept:
      ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  },
  {
    kind: "image",
    label: "Images",
    sub: "JPG, PNG",
    accent: "#7B1FA2",
    Icon: ImageRoundedIcon,
    accept: "image/*",
  },
  {
    kind: "audio",
    label: "Audio",
    sub: "MP3, M4A",
    accent: "#00897B",
    Icon: GraphicEqRoundedIcon,
    accept: "audio/*",
  },
];

const STEP_TITLES = [
  "What are you studying?",
  "Set your goal",
  "Level & formats",
  "Add materials",
  "Confirm plan",
];

function uploadKindFor(file) {
  if (file.kind) return file.kind;
  const name = String(file.name || "").toLowerCase();
  const mime = String(file.type || "").toLowerCase();
  if (mime.includes("pdf") || name.endsWith(".pdf")) return "pdf";
  if (mime.includes("presentation") || name.endsWith(".ppt") || name.endsWith(".pptx")) return "ppt";
  if (mime.includes("word") || mime.includes("wordprocessing") || name.endsWith(".doc") || name.endsWith(".docx")) {
    return "doc";
  }
  if (mime.startsWith("audio") || /\.(mp3|m4a|wav)$/.test(name)) return "audio";
  return "image";
}

export default function StudyPlanCreate() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preset = params.get("subject") || "Histology";
  const [step, setStep] = useState(0);
  const [subject, setSubject] = useState(STUDY_SUBJECTS.includes(preset) ? preset : "Histology");
  const [customSubject, setCustomSubject] = useState(STUDY_SUBJECTS.includes(preset) ? "" : preset);
  const [goal, setGoal] = useState("exam");
  const [examName, setExamName] = useState("");
  const [examDate, setExamDate] = useState("");
  const [language, setLanguage] = useState("en");
  const [level, setLevel] = useState("intermediate");
  const [targetMastery, setTargetMastery] = useState("practiced");
  const [preferences, setPreferences] = useState(["smart_text", "practice"]);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const fileInputsRef = useRef({});
  const pendingPlanId = useRef(null);
  const uploadedKeys = useRef(new Set());

  const subjectName = (customSubject || subject).trim();
  const hasUploads = files.length > 0;

  const changeSubject = (next) => {
    setSubject(next);
    setCustomSubject("");
  };

  const togglePref = (id) => {
    setPreferences((current) => {
      const has = current.includes(id);
      if (has && current.length === 1) return current;
      return has ? current.filter((item) => item !== id) : [...current, id];
    });
  };

  const openPicker = (kind) => {
    if (saving) return;
    const input = fileInputsRef.current[kind];
    if (!input) return;
    input.value = "";
    input.click();
  };

  const onFileChosen = (kind, event) => {
    const picked = event.target.files?.[0];
    if (!picked) return;
    if (picked.size > MAX_UPLOAD_BYTES) {
      const mb = (picked.size / (1024 * 1024)).toFixed(1);
      setError(`This file is ${mb} MB. Maximum upload size is ${MAX_UPLOAD_MB} MB.`);
      return;
    }
    setError("");
    setFiles((current) => [
      ...current,
      {
        key: `${picked.name}-${picked.size}-${picked.lastModified}-${Date.now()}`,
        name: picked.name,
        type: picked.type,
        size: picked.size,
        kind,
        file: picked,
      },
    ]);
  };

  const removeFile = (key) => {
    setFiles((current) => current.filter((item) => item.key !== key));
  };

  const canNext = useMemo(() => {
    if (step === 0) return Boolean(subjectName);
    if (step === 1) return Boolean(goal && language);
    if (step === 2) return preferences.length > 0;
    if (step === 3) return hasUploads;
    return hasUploads;
  }, [goal, language, preferences.length, step, subjectName, hasUploads]);

  const save = async () => {
    if (!hasUploads) {
      setError("Upload a medical file to continue. A PDF, PowerPoint, Word document, image, or audio file is enough.");
      return;
    }
    setSaving(true);
    setError("");
    setSaveStatus("Creating your study plan…");

    const body = {
      subject: subjectName,
      goal,
      examName: examName.trim(),
      materialLanguage: language,
      examDate,
      level,
      targetMastery,
      learnWithAi: true,
      preferences,
      extractTopics: true,
      topics: [],
    };

    let planId = pendingPlanId.current;
    if (!planId) {
      const response = await createStudyPlanCall(body);
      if (response?.code === "INSUFFICIENT_CREDITS" || response?.status === 402) {
        setSaving(false);
        setSaveStatus("");
        setError(response.message || "You need a study credit to start a subject.");
        return;
      }
      if (!response?.plan?.id) {
        setSaving(false);
        setSaveStatus("");
        setError(response?.message || "The study plan could not be created.");
        return;
      }
      planId = response.plan.id;
      pendingPlanId.current = planId;
    }

    const pending = files.filter((item) => !uploadedKeys.current.has(item.key));
    let uploadedCount = 0;
    for (const item of files) {
      if (uploadedKeys.current.has(item.key)) continue;
      uploadedCount += 1;
      setSaveStatus(`Uploading ${item.name} · ${uploadedCount}/${pending.length || files.length}`);
      const uploaded = await uploadStudyPlanMaterialCall(planId, item.file);
      if (!uploaded?.material?.id) {
        setSaving(false);
        setSaveStatus("");
        setError(uploaded?.message || "Could not upload your study material. Try again.");
        return;
      }
      uploadedKeys.current.add(item.key);
    }

    setSaveStatus("Building topics from your files…");
    const extracted = await extractStudyTopicsCall(planId);
    if (!extracted?.plan?.topics?.length) {
      setSaving(false);
      setSaveStatus("");
      setError(extracted?.message || "Could not extract topics from your files.");
      return;
    }

    setSaving(false);
    setSaveStatus("");
    navigate(`/study/plan/${extracted.plan.id}`);
  };

  const goalLabel = GOALS.find((item) => item.id === goal)?.title || goal;
  const levelLabel = LEVELS.find((item) => item.id === level)?.label || level;
  const masteryLabel = MASTERY.find((item) => item.id === targetMastery)?.label || targetMastery;
  const languageLabel = MATERIAL_LANGUAGES.find(([code]) => code === language)?.[1] || language;

  return (
    <StudyShell>
      <div className="study-wizard">
        <div className="study-section" style={{ marginTop: 0 }}>
          <h2>New study plan</h2>
          <button type="button" onClick={() => navigate("/study")}>Cancel</button>
        </div>
        <div className="study-steps" aria-hidden>
          {STEP_TITLES.map((_, index) => (
            <span key={index} className={index <= step ? "on" : ""} />
          ))}
        </div>
        <p className="study-meta" style={{ marginBottom: 14 }}>{STEP_TITLES[step]}</p>

        {step === 0 ? (
          <>
            {STUDY_SUBJECTS.map((item) => (
              <button
                key={item}
                type="button"
                className={subject === item && !customSubject ? "study-choice selected" : "study-choice"}
                onClick={() => changeSubject(item)}
              >
                <strong>{item}</strong>
              </button>
            ))}
            <label className="study-meta">
              Another medical course
              <input
                className="study-field"
                value={customSubject}
                placeholder="Course name"
                onChange={(event) => setCustomSubject(event.target.value)}
              />
            </label>
          </>
        ) : null}

        {step === 1 ? (
          <>
            {GOALS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={goal === item.id ? "study-choice selected" : "study-choice"}
                onClick={() => setGoal(item.id)}
              >
                <strong>{item.title}</strong>
                <small>{item.body}</small>
              </button>
            ))}
            <label className="study-meta">
              Exam name
              <input
                className="study-field"
                value={examName}
                placeholder="Optional"
                onChange={(event) => setExamName(event.target.value)}
              />
            </label>
            <label className="study-meta">
              Exam date
              <input
                className="study-field"
                type="date"
                value={examDate}
                onChange={(event) => setExamDate(event.target.value)}
              />
            </label>
            <label className="study-meta">
              Lesson language
              <select
                className="study-field"
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
              >
                {MATERIAL_LANGUAGES.map(([code, label]) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            </label>
          </>
        ) : null}

        {step === 2 ? (
          <>
            <p className="study-meta">Your level</p>
            {LEVELS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={level === item.id ? "study-choice selected" : "study-choice"}
                onClick={() => setLevel(item.id)}
              >
                <strong>{item.emoji} {item.label}</strong>
              </button>
            ))}
            <p className="study-meta">Target</p>
            {MASTERY.map((item) => (
              <button
                key={item.id}
                type="button"
                className={targetMastery === item.id ? "study-choice selected" : "study-choice"}
                onClick={() => setTargetMastery(item.id)}
              >
                <strong>{item.emoji} {item.label}</strong>
              </button>
            ))}
            <p className="study-meta">Formats</p>
            {PREFS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={preferences.includes(item.id) ? "study-choice selected" : "study-choice"}
                onClick={() => togglePref(item.id)}
              >
                <strong>{item.label}</strong>
              </button>
            ))}
          </>
        ) : null}

        {step === 3 ? (
          <>
            <div className="study-upload-limit">
              <CloudUploadRoundedIcon fontSize="small" />
              <span>Maximum {MAX_UPLOAD_MB} MB per file. Topics will be taken from your files.</span>
            </div>
            <div className="study-upload-grid">
              {UPLOAD_OPTIONS.map((opt) => {
                const Icon = opt.Icon;
                return (
                  <button
                    key={opt.kind}
                    type="button"
                    className="study-upload-tile"
                    disabled={saving}
                    onClick={() => openPicker(opt.kind)}
                    style={{ "--upload-accent": opt.accent }}
                  >
                    <span className="study-upload-icon" style={{ background: `${opt.accent}18`, color: opt.accent }}>
                      <Icon fontSize="small" />
                    </span>
                    <strong>{opt.label}</strong>
                    <small>{opt.sub}</small>
                  </button>
                );
              })}
            </div>
            {UPLOAD_OPTIONS.map((opt) => (
              <input
                key={opt.kind}
                ref={(node) => {
                  fileInputsRef.current[opt.kind] = node;
                }}
                type="file"
                accept={opt.accept}
                hidden
                onChange={(event) => onFileChosen(opt.kind, event)}
              />
            ))}
            {files.map((file) => {
              const kind = uploadKindFor(file);
              const opt = UPLOAD_OPTIONS.find((item) => item.kind === kind) || UPLOAD_OPTIONS[0];
              const Icon = opt.Icon;
              return (
                <div className="study-file-row" key={file.key}>
                  <span className="study-upload-icon" style={{ background: `${opt.accent}18`, color: opt.accent }}>
                    <Icon fontSize="small" />
                  </span>
                  <strong title={file.name}>{file.name}</strong>
                  <button type="button" aria-label={`Remove ${file.name}`} onClick={() => removeFile(file.key)}>
                    <CloseRoundedIcon fontSize="small" />
                  </button>
                </div>
              );
            })}
            <p className="study-meta" style={{ marginTop: 12 }}>
              {hasUploads
                ? "Topics will be taken from your files."
                : "Upload a medical file to continue. A PDF, PowerPoint, Word document, image, or audio file is enough."}
            </p>
          </>
        ) : null}

        {step === 4 ? (
          <div className="study-review">
            <div className="study-review-line">
              <span>Subject</span>
              <strong>{subjectName}</strong>
            </div>
            <div className="study-review-line">
              <span>Goal</span>
              <strong>{goalLabel}</strong>
            </div>
            <div className="study-review-line">
              <span>Language</span>
              <strong>{languageLabel}</strong>
            </div>
            <div className="study-review-line">
              <span>Level</span>
              <strong>{levelLabel}</strong>
            </div>
            <div className="study-review-line">
              <span>Target</span>
              <strong>{masteryLabel}</strong>
            </div>
            <div className="study-review-line">
              <span>Materials</span>
              <strong>{files.map((file) => file.name).join(", ")}</strong>
            </div>
            <div className="study-review-line">
              <span>Topics</span>
              <strong>Taken from your files</strong>
            </div>
            <p className="study-meta" style={{ marginTop: 8 }}>
              Lessons use your uploads plus Microlab’s medical library. Always verify against your course materials.
            </p>
          </div>
        ) : null}

        {error ? <div className="study-error">{error}</div> : null}
        {saving && saveStatus ? (
          <div className="study-save-status">
            <CircularProgress size={16} />
            <span>{saveStatus}</span>
          </div>
        ) : null}

        <div className="study-hero-actions">
          {step > 0 ? (
            <button
              type="button"
              className="study-choice"
              style={{ width: "auto" }}
              disabled={saving}
              onClick={() => setStep(step - 1)}
            >
              Back
            </button>
          ) : null}
          {step < 4 ? (
            <button
              type="button"
              className="study-primary"
              disabled={!canNext || saving}
              onClick={() => {
                if (!canNext) return;
                if (step === 3 && !hasUploads) {
                  setError("Upload a medical file to continue. A PDF, PowerPoint, Word document, image, or audio file is enough.");
                  return;
                }
                setError("");
                setStep(step + 1);
              }}
              style={{ background: "#1e1b4b", color: "#fff" }}
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              className="study-primary"
              disabled={saving || !canNext}
              onClick={save}
              style={{ background: "#1e1b4b", color: "#fff" }}
            >
              {saving ? <CircularProgress size={16} color="inherit" /> : "Create plan"}
            </button>
          )}
        </div>
      </div>
    </StudyShell>
  );
}
